"use client";

import { FC, ReactNode, forwardRef } from "react";
import { cn } from "utils";

/** HTML `.wave-chunk` — inline-block 供 GSAP 做 y */
const waveChunkClass = "inline-block max-w-none opacity-0 align-baseline";

/** 高亮段与前后文案间距 */
const HEADLINE_ROW_GAP_CLASS =
  "inline-flex max-w-full flex-wrap items-baseline justify-center gap-x-1";

/** HTML `.number-roller.tabular-numbers` */
const NumberRoller: FC<{ initial?: number }> = ({ initial = 0 }) => (
  <span className="number-roller inline-block min-w-[0.6ch] text-center tabular-nums">
    {initial}
  </span>
);

interface TargetCountBlockProps {
  quantifier: string;
}

/**
 * `#target-count`：单层渐变字，CSS keyframes 驱动 primary → accent4 混色
 */
const TargetCountBlock: FC<TargetCountBlockProps> = ({ quantifier }) => (
  <span
    id="target-count"
    className={cn(
      waveChunkClass,
      "relative inline-block select-none align-baseline font-semibold",
    )}
    data-onboarding-wave-chunk
    data-testid="onboarding-welcome-items"
  >
    <span
      className="onboarding-welcome-accent-phrase tabular-nums"
      data-testid="onboarding-welcome-items-accent"
    >
      <NumberRoller />
      <span> {quantifier}</span>
    </span>
  </span>
);

interface OnboardingWelcomeHeadlineProps {
  prefix: string;
  suffix: string;
  quantifier: string;
}

/** HTML `h2.title-container` */
export const OnboardingWelcomeHeadline: FC<OnboardingWelcomeHeadlineProps> = ({
  prefix,
  suffix,
  quantifier,
}) => {
  const prefixText = prefix.trimEnd();
  const suffixText = suffix.trimStart();

  return (
    <h2
      className="title-container headline_regular_m relative mx-auto max-w-[300px] text-center leading-[1.24] tracking-tight text-content_primary"
      data-testid="onboarding-welcome-headline"
    >
      <span className={HEADLINE_ROW_GAP_CLASS}>
        {prefixText ? (
          <span className={waveChunkClass} data-onboarding-wave-chunk>
            {prefixText}
          </span>
        ) : null}
        <TargetCountBlock quantifier={quantifier} />
        {suffixText ? (
          <span className={waveChunkClass} data-onboarding-wave-chunk>
            {suffixText}
          </span>
        ) : null}
      </span>
    </h2>
  );
};

interface OnboardingWelcomeWaveTextProps {
  className?: string;
  "data-testid"?: string;
  children: ReactNode;
}

/** 问候 / 副标题 `.wave-chunk` */
export const OnboardingWelcomeWaveText: FC<OnboardingWelcomeWaveTextProps> = ({
  className,
  "data-testid": testId,
  children,
}) => (
  <div
    className={cn(waveChunkClass, className)}
    data-onboarding-wave-chunk
    data-testid={testId}
  >
    {children}
  </div>
);

interface OnboardingWelcomeCtaProps {
  className?: string;
  onClick?: () => void;
  children: ReactNode;
  "data-openreplay-label"?: string;
  "data-testid"?: string;
}

/** HTML `#main-action` — 仅 GSAP 一次入场，无呼吸动画 */
export const OnboardingWelcomeCta = forwardRef<
  HTMLButtonElement,
  OnboardingWelcomeCtaProps
>(function OnboardingWelcomeCta(
  {
    className,
    onClick,
    children,
    "data-openreplay-label": openReplayLabel,
    "data-testid": testId,
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      id="main-action"
      className={cn(className, "relative overflow-hidden opacity-0")}
      onClick={onClick}
      data-onboarding-stream-cta
      data-openreplay-label={openReplayLabel}
      data-testid={testId}
    >
      {children}
    </button>
  );
});
